const VIEW_PREFS_KEY = 'nobgp.graphView'

// `hidePhoneCard`: the noBGP app's "Add this phone" card on the Nodes list, dismissed (usePhoneNode).
export type ViewPref = 'mapView' | 'hidePhoneCard'

// The map view starts off: the graph is the view every reader knows, and the map is a choice.
//
// ⚠ `hideServices` and `hideTargets` were the two other preferences, and they are gone (Benoit,
// 2026-10-02): a node's services and names are shown when the node is pointed at or selected, and
// there is no switch to show all of them. A value of theirs still in storage is not read.
const DEFAULTS: Record<ViewPref, boolean> = {
  mapView: false,
  hidePhoneCard: false,
}

// ⚠⚠ GUARDED, and NOT for the reason `lib/rememberedEmail.ts` is guarded — the blast
// radius is different and larger. `localStorage` throws on mere ACCESS in a browser told
// to block site data, and these two are read while the Zustand store MODULE is evaluated,
// which is import time: before a render, before an ErrorBoundary could exist (the app has
// none), and before the user has done anything. An unguarded read there does not lose a
// preference, it takes the whole app down with a blank page — the same failure mode as the
// duplicate-channel crash in app#132's neighbourhood. A lost preference costs one click.
//
// ⚠ `graph/state.ts` reads storage at module scope for the same reason and is now guarded the
// same way (app#311). Any NEW module-scope reader needs the same treatment — the cost of missing
// one is a blank app, not a lost preference.
function readAll(): Partial<Record<ViewPref, boolean>> {
  try {
    const raw = localStorage.getItem(VIEW_PREFS_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    return parsed as Partial<Record<ViewPref, boolean>>
  } catch {
    return {}
  }
}

/**
 * The stored value, or the default when nothing is stored.
 *
 * ⚠ The `typeof` check is load-bearing rather than defensive ceremony: this key is written
 * by this app and read by this app, but a half-written or hand-edited entry would otherwise
 * make `mapView` the string `"false"` — truthy — and draw the map the reader just turned off,
 * with no way to get back except clearing site data.
 */
export function readViewPref(name: ViewPref): boolean {
  const value = readAll()[name]
  return typeof value === 'boolean' ? value : DEFAULTS[name]
}

export function writeViewPref(name: ViewPref, value: boolean): void {
  try {
    localStorage.setItem(VIEW_PREFS_KEY, JSON.stringify({ ...readAll(), [name]: value }))
  } catch {
    // The switch still works for this session; it is only not remembered for the next one.
  }
}
